<script setup>
import { ref, computed, watch } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import FormSelect from '@/components/Atoms/FormSelect.vue'
import FormSwitch from '@/components/Atoms/FormSwitch.vue'
import FormTextarea from '@/components/Atoms/FormTextarea.vue'
import Alert from '@/components/Atoms/Alert.vue'
import Spinner from '@/components/ui/Spinner.vue'
import BroadcastPresetsEditor from './BroadcastPresetsEditor.vue'
import { useSiteGiving } from '@/composables/useSiteGiving'
import { isFeatureDisabled } from '@/services/SiteAdminService'
import { GIVING_MODES, GIVING_DEFAULT_BUTTON_LABEL, GIVING_DEFAULT_THANK_YOU, GIVING_CURRENCIES, GIVING_AMOUNT_CAP, GIVING_MIN_AMOUNT_FLOOR, GIVING_PRESETS_MAX } from '@/components/Sites/siteTemplates'

// Settings › Broadcast › Giving. One form, one save: castr's
// `PUT /sites/{id}/giving` writes the presentation keys to the runtime (draft)
// and, for Donations, upserts the paywall donation product that holds the
// amounts (contract D5). The giving *message* is part of Service info.
const props = defineProps({
	siteId: {
		type: String,
		required: true,
	},
	/** Owner's Stripe Connect state when known (castr /monetization); null = unknown. */
	stripeConnected: {
		type: Boolean,
		default: null,
	},
	/** Currency to start a new donation product with (owner's plans), else USD. */
	defaultCurrency: {
		type: String,
		default: 'USD',
	},
})
const emit = defineEmits(['saved'])
const { notify } = useNotification()
const giving = useSiteGiving(() => props.siteId)

const loading = computed(() => !giving.state.value.loaded)
const loadError = computed(() => giving.state.value.error)
const config = computed(() => giving.giving.value)
// A runtime that predates the giving keys answers 400 for them.
const writable = computed(() => config.value?.writable !== false)
// Native giving is always offered (no feature flag); when castr knows it would
// not work right now the card says so instead of hiding the option.
const nativeNotice = computed(() => {
	const c = config.value
	if (!c) return null
	switch (c.nativeUnavailable) {
	case 'donations_disabled':
		return { reason: 'donations_disabled', text: 'Donations are switched off on this Castr server; ask support to turn them back on.' }
	case 'paywall_donations_disabled':
		return { reason: 'paywall_donations_disabled', text: 'Donations are not enabled on the payment service yet. Saving will fail until they are; the site shows "Giving is temporarily unavailable" in the meantime.' }
	case 'paywall_unavailable':
		return { reason: 'paywall_unavailable', text: `The payment service could not be reached${c.nativeError ? ` (${c.nativeError})` : ''}. Saved donation settings are not shown; try again in a minute.` }
	default:
		return c.caps?.donations === false ? { reason: 'donations_disabled', text: 'Donations are switched off on this Castr server.' } : null
	}
})

const DEFAULT_PRESETS = [10, 25, 50, 100]
const emptyForm = () => ({
	mode: 'off',
	buttonLabel: '',
	thankYouMessage: '',
	native: { presets: [...DEFAULT_PRESETS], minAmount: GIVING_MIN_AMOUNT_FLOOR, currency: props.defaultCurrency || 'USD', customAmountEnabled: true },
})
const form = ref(emptyForm())
const fillForm = () => {
	const c = config.value
	const f = emptyForm()
	if (c) {
		f.mode = giving.mode.value
		f.buttonLabel = c.buttonLabel && c.buttonLabel !== GIVING_DEFAULT_BUTTON_LABEL ? c.buttonLabel : ''
		f.thankYouMessage = c.thankYouMessage && c.thankYouMessage !== GIVING_DEFAULT_THANK_YOU ? c.thankYouMessage : ''
		if (c.native) {
			f.native = {
				presets: Array.isArray(c.native.presets) && c.native.presets.length ? [...c.native.presets] : [...DEFAULT_PRESETS],
				minAmount: Number(c.native.minAmount) || GIVING_MIN_AMOUNT_FLOOR,
				currency: c.native.currency || props.defaultCurrency || 'USD',
				customAmountEnabled: c.native.customAmountEnabled !== false,
			}
		}
	}
	form.value = f
}
watch(config, fillForm, { immediate: true })
giving.load()

const modes = computed(() => GIVING_MODES.map((m) => ({ ...m })))
const setMode = (id) => { form.value.mode = id }

const currencyOptions = computed(() => {
	const list = GIVING_CURRENCIES.includes(form.value.native.currency) || !form.value.native.currency ? GIVING_CURRENCIES : [form.value.native.currency, ...GIVING_CURRENCIES]
	return list.map((c) => ({ value: c, text: c }))
})
const onMinAmount = (value) => {
	const n = Number(String(value ?? '').replace(',', '.'))
	form.value.native.minAmount = Number.isFinite(n) ? n : value
}

// --- validation (mirrors castr sites-giving.js) --------------------------------
const errors = computed(() => {
	const e = {}
	const f = form.value
	if (String(f.buttonLabel || '').trim().length > 24) e.buttonLabel = 'Up to 24 characters'
	if (String(f.thankYouMessage || '').length > 500) e.thankYouMessage = 'Up to 500 characters'
	if (f.mode === 'native') {
		const n = f.native
		const min = Number(n.minAmount)
		if (!Number.isFinite(min) || min < GIVING_MIN_AMOUNT_FLOOR || min > GIVING_AMOUNT_CAP || Math.round(min * 100) !== min * 100) e.minAmount = `Between ${GIVING_MIN_AMOUNT_FLOOR} and ${GIVING_AMOUNT_CAP}, at most 2 decimals`
		if (!/^[A-Z]{3}$/.test(String(n.currency || ''))) e.currency = 'Pick a currency'
		if (!n.presets.length) e.presets = 'Add at least one amount'
		else if (n.presets.length > GIVING_PRESETS_MAX) e.presets = `Up to ${GIVING_PRESETS_MAX} amounts`
		else if (Number.isFinite(min) && n.presets.some((p) => p < min)) e.presets = `Every amount must be at least the minimum (${min})`
		else if (n.presets.some((p) => p > GIVING_AMOUNT_CAP)) e.presets = `Amounts are capped at ${GIVING_AMOUNT_CAP}`
	}
	return e
})
const valid = computed(() => Object.keys(errors.value).length === 0)

// --- changes ------------------------------------------------------------------
const norm = (v) => String(v ?? '').trim()
const dirty = computed(() => {
	const c = config.value
	if (!c) return form.value.mode !== 'off'
	const f = form.value
	if (f.mode !== giving.mode.value) return true
	if ((norm(f.buttonLabel) || GIVING_DEFAULT_BUTTON_LABEL) !== (c.buttonLabel || GIVING_DEFAULT_BUTTON_LABEL)) return true
	if ((norm(f.thankYouMessage) || GIVING_DEFAULT_THANK_YOU) !== (c.thankYouMessage || GIVING_DEFAULT_THANK_YOU)) return true
	if (f.mode === 'native') {
		const n = c.native
		if (!n) return true
		if (Number(f.native.minAmount) !== Number(n.minAmount)) return true
		if (f.native.currency !== n.currency) return true
		if (f.native.customAmountEnabled !== (n.customAmountEnabled !== false)) return true
		if (JSON.stringify(f.native.presets) !== JSON.stringify(n.presets || [])) return true
	}
	return false
})

const saving = ref(false)
const save = async () => {
	if (!valid.value || saving.value || !dirty.value || !writable.value) return
	saving.value = true
	const f = form.value
	const body = {
		mode: f.mode,
		buttonLabel: norm(f.buttonLabel) || null,
		thankYouMessage: norm(f.thankYouMessage) || null,
		target: 'draft',
	}
	if (f.mode === 'native') {
		body.native = {
			presets: [...f.native.presets].sort((a, b) => a - b),
			minAmount: Number(f.native.minAmount),
			currency: f.native.currency,
			customAmountEnabled: f.native.customAmountEnabled !== false,
		}
	}
	try {
		const res = await giving.save(body)
		notify({ group: 'success', text: f.mode === 'native' ? 'Giving saved. Donations are on once you publish.' : 'Giving saved. Publish when you are ready.' })
		emit('saved', res)
	} catch (err) {
		let text = err.message || 'Could not save giving.'
		if (isFeatureDisabled(err)) text = 'Donations are not enabled on the payment service yet — try again later or ask support.'
		else if (err.reason === 'stripe_inactive') text = 'Connect Stripe under Settings › General before turning donations on.'
		else if (err.reason === 'runtime_settings_failed') text = 'The donation product was saved but the site settings were not — save again.'
		else if (err.reason === 'validation_failed' && err.payload?.details) text = Object.entries(err.payload.details).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('; ')
		notify({ group: 'error', text })
	}
	saving.value = false
}
const discard = () => fillForm()

const stripeHint = computed(() => (form.value.mode === 'native' && props.stripeConnected === false ? 'Stripe is not connected. Connect it under Settings › General — donations are paid out to your Stripe account.' : ''))
</script>

<template>
	<section class="rounded-lg bg-surface-2 p-4 sm:p-5" data-testid="broadcast-giving" :data-mode="form.mode">
		<div class="mb-4 flex flex-wrap items-start justify-between gap-2">
			<div class="min-w-0">
				<h3 class="mb-1 text-sm font-semibold text-white">Giving</h3>
				<p class="mb-0 text-tiny text-surface-7">The Give button in the header, under the player and in the About block.</p>
			</div>
		</div>

		<Spinner v-if="loading && !loadError" text="Loading giving…" classes="text-surface-8 my-6" spinner-color="var(--c-dark-8)" spinner-size="15px" />
		<Alert v-else-if="loadError" type="error" class="!mb-0">{{ loadError.message || 'Could not load the giving settings.' }}</Alert>
		<form v-else class="flex flex-col gap-4" data-testid="giving-form" @submit.prevent="save">
			<p v-if="!writable" class="mb-0 rounded-md bg-surface-3 px-3 py-2 text-tiny text-surface-7" data-testid="giving-unsupported">Giving needs a newer site runtime — it appears here as soon as your site is updated.</p>

			<div role="radiogroup" aria-label="Giving mode" class="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="giving-modes">
				<button v-for="m in modes" :key="m.id" type="button" role="radio" :aria-checked="form.mode === m.id" :disabled="!writable"
					class="min-w-0 rounded-lg border p-3 text-left transition-colors disabled:cursor-not-allowed"
					:class="form.mode === m.id ? 'border-main bg-main-transparent' : 'border-surface-4 bg-surface-3 hover:border-surface-6'"
					:data-testid="`giving-mode-${m.id}`" @click="setMode(m.id)">
					<span class="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-medium text-white">
						<span>{{ m.label }}</span>
						<span v-if="m.id === 'native' && nativeNotice" class="shrink-0 whitespace-nowrap rounded-full bg-surface-4 px-1.5 py-px text-[10px] font-medium uppercase tracking-wide text-surface-8" data-testid="giving-native-badge">Unavailable</span>
					</span>
					<span class="mt-0.5 block text-tiny text-surface-7">{{ m.description }}</span>
				</button>
			</div>

			<!-- Native donations -->
			<div v-if="form.mode === 'native'" class="flex flex-col gap-4" data-testid="giving-native">
				<Alert v-if="nativeNotice" type="warning" class="!mb-0" data-testid="giving-native-notice" :data-reason="nativeNotice.reason">{{ nativeNotice.text }}</Alert>
				<Alert v-if="stripeHint" type="warning" class="!mb-0" data-testid="giving-stripe-hint">{{ stripeHint }}</Alert>
				<BroadcastPresetsEditor v-model="form.native.presets" :currency="form.native.currency" :disabled="!writable" :error="errors.presets" />
				<div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
					<div class="min-w-0">
						<FormInput id="giving-min-amount" :model-value="form.native.minAmount" label="Minimum amount" type="number" :disabled="!writable" :is-error="Boolean(errors.minAmount)" class="min-w-0" @update:model-value="onMinAmount" />
						<p class="mb-0 mt-1.5 text-tiny" :class="errors.minAmount ? 'text-error' : 'text-surface-7'">{{ errors.minAmount || 'The smallest gift accepted.' }}</p>
					</div>
					<div class="min-w-0">
						<FormSelect id="giving-currency" v-model="form.native.currency" label="Currency" :options="currencyOptions" :disabled="!writable || Boolean(config?.native)" class="min-w-0" />
						<p class="mb-0 mt-1.5 text-tiny" :class="errors.currency ? 'text-error' : 'text-surface-7'">{{ errors.currency || (config?.native ? 'Fixed once donations have been set up.' : 'The currency donors give in.') }}</p>
					</div>
				</div>
				<div class="rounded-lg bg-surface-3 px-3 py-2.5">
					<FormSwitch id="giving-custom-amount" v-model="form.native.customAmountEnabled" label="Allow a custom amount" label-position="left" label-styles="w-full justify-between gap-3 text-sm text-surface-text-1" :disabled="!writable" />
					<p class="mb-0 mt-0.5 pr-14 text-tiny text-surface-7">An “Other amount” field next to the suggested amounts.</p>
				</div>
				<div class="min-w-0">
					<FormTextarea id="giving-thank-you" v-model="form.thankYouMessage" label="Thank-you message" :placeholder="GIVING_DEFAULT_THANK_YOU" :rows="2" :max-rows="4" :maxlength="500" :disabled="!writable" :is-error="Boolean(errors.thankYouMessage)" hint="Shown after a gift and in the receipt e-mail. Leave empty for the default." />
					<p v-if="errors.thankYouMessage" class="mb-0 mt-1.5 text-tiny text-error">{{ errors.thankYouMessage }}</p>
				</div>
			</div>

			<!-- Shared: button label -->
			<div v-if="form.mode !== 'off'" class="min-w-0">
				<FormInput id="giving-button-label" v-model="form.buttonLabel" label="Button label" :placeholder="GIVING_DEFAULT_BUTTON_LABEL" input-classes="placeholder:text-surface-6" :maxlength="24" :disabled="!writable" :is-error="Boolean(errors.buttonLabel)" class="min-w-0 sm:w-64" />
				<p class="mb-0 mt-1.5 text-tiny" :class="errors.buttonLabel ? 'text-error' : 'text-surface-7'">{{ errors.buttonLabel || `Leave empty for “${GIVING_DEFAULT_BUTTON_LABEL}”. Also “Donate”, “Tithe”, “Support us”…` }}</p>
			</div>

			<div class="flex flex-wrap items-center gap-3 border-t border-surface-3 pt-4">
				<p class="mb-0 mr-auto text-tiny text-surface-7" data-testid="giving-status">
					<template v-if="dirty">Unsaved giving changes.</template>
					<template v-else-if="config?.draftKeys?.length">Saved to the draft — publish to make it live.</template>
					<template v-else-if="config?.native && config.mode === 'native'">Donations are on. Donors see {{ config.native.presets.length }} suggested {{ config.native.presets.length === 1 ? 'amount' : 'amounts' }} in {{ config.native.currency }}.</template>
					<template v-else>Everything saved.</template>
				</p>
				<FormButton v-if="dirty" type="link" size="sm" :disabled="saving" data-testid="giving-discard" @click="discard">Discard</FormButton>
				<FormButton type="main" size="md" :disabled="!dirty || !valid || saving || !writable" :is-loading="saving" data-testid="giving-save" @click="save">Save giving</FormButton>
			</div>
		</form>
	</section>
</template>
